import { AxiosResponse } from 'axios';
import { Fragment, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { Operator } from 'shell/models/operator';
import FormOperator from 'system/components/operators/form';
import { OperatorForm } from 'system/view-models/operator';
import { FEATURE_AUTHORIZATION_ANPR } from '../form/constants';
import { transformerOperator } from '../utils/transformer-operator';

interface OperatorEditProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  id?: string;
}

const OperatorEdit = ({ onSuccess, onCancel, id }: OperatorEditProps): React.ReactElement => {
  const params = useParams();

  const [operator] = useHttpFetch<HttpResponse<Operator>, OperatorForm>(
    `${__MANAGEMENT_API_URL__}/operatori/${id || params.id}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
      },
      transformer: (response: AxiosResponse<HttpResponse<Operator>>) =>
        transformerOperator(response),
    }
  );

  const updateOperator = useHttpClient(`${__MANAGEMENT_API_URL__}/operatori/${id || params.id}`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
      data: {},
    },
  });

  const initialValues: OperatorForm | undefined = useMemo(() => {
    const isRequiredAnpr = (operator.data as OperatorForm)?.feature?.authorizationsList?.find(
      (item) =>
        Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR &&
        (item.value?.split(' ')[0] === '1' || item.value?.split(' ')[0] === '2')
    )?.value;

    return {
      ...((operator.data as OperatorForm) || {}),
      anpr: {
        ...((operator.data as OperatorForm)?.anpr || {}),
        isRequiredAnpr: Boolean(isRequiredAnpr),
        isEditAnprData:
          (operator.data as OperatorForm)?.anpr?.anprOperatorCode ||
          (operator.data as OperatorForm)?.anpr?.validityStart ||
          (operator.data as OperatorForm)?.anpr?.validityEnd ||
          (operator.data as OperatorForm)?.anpr?.stationIdAnpr
            ? true
            : false,
      },
    } as OperatorForm;
  }, [operator.data, operator.isLoading, operator.pristine]);

  const onSubmit = useCallback(
    (formValues: OperatorForm | undefined) => {
      (
        updateOperator({
          data: {
            key: {
              codiceOperatore: formValues?.operator?.code || undefined,
              codiceEnte: formValues?.operator?.institution || undefined,
            },
            timestampUltimoAccesso: formValues?.operator?.lastLoginDateTime,
            dataCancellazione: formValues?.operator?.cancellationDate?.toFormat('yyyy-MM-dd'),
            codiceQualifica: formValues?.operator?.qualification || undefined,
            operatoreProfiloList: [
              {
                key: {
                  codiceProfilo: formValues?.operator?.profile || undefined,
                  codiceOperatore: formValues?.operator?.code || undefined,
                  codiceEnte: formValues?.operator?.institution || undefined,
                },
              },
            ],
            note: formValues?.operator?.lastUpdate || undefined,
            cognomeNome: formValues?.operator?.name || undefined,
            codiceFiscale:
              (initialValues?.operator?.fiscalCode || '') === formValues?.operator?.fiscalCode
                ? undefined
                : formValues?.operator?.fiscalCode || '',
            tipoOperatore: formValues?.operator?.typeOperator || undefined,
            codiceServizio: formValues?.operator?.service || undefined,
            dataScadenza: formValues?.operator?.authorizationExpiration || undefined,
            userEstesa: formValues?.operator?.extCode || undefined,
            codana: formValues?.operator?.codAna
              ? Number.parseInt(formValues?.operator?.codAna, 10)
              : undefined,
            email: formValues?.operator?.email || undefined,
            dominio: formValues?.operator?.organizationEmail || undefined,
            operatoreServizioList: (formValues?.services?.servicesList || []).map((service) => {
              return {
                key: {
                  codiceOperatore: formValues?.operator?.code,
                  codiceServizio: service,
                  codiceEnte: formValues?.operator?.institution,
                },
                // servizio: vedere se va aggiunto
              };
            }),
            tipoFeatureAuthOperatoreList: formValues?.feature?.authorizationsList?.map((item) => {
              return {
                key: {
                  codiceFeature: item.id || undefined,
                  codiceOperatore: formValues?.operator?.code || undefined,
                  codiceEnte: formValues?.operator?.institution || undefined,
                },
                valoreFeatureAuthOpe: item.value.split(' ')[0],
              };
            }),
            operatoreAnpr:
              formValues?.anpr?.validityEnd ||
              formValues?.anpr?.validityStart ||
              formValues?.anpr?.stationIdAnpr
                ? {
                    key: {
                      codice: formValues?.operator?.code,
                      codiceEnte: formValues?.operator?.institution,
                    },
                    codiceAnpr: formValues.anpr.anprOperatorCode || undefined,
                    dataInizioValidita: formValues?.anpr?.validityStart
                      ? formValues?.anpr?.validityStart?.toFormat('yyyy-MM-dd')
                      : undefined,
                    dataFineValidita: formValues?.anpr?.validityEnd
                      ? formValues?.anpr?.validityEnd?.toFormat('yyyy-MM-dd')
                      : undefined,
                    idPostazione: formValues?.anpr?.stationIdAnpr?.id || undefined,
                  }
                : undefined,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        if (onSuccess) {
          onSuccess();
        }
      });
    },
    [initialValues]
  );

  return (
    <Fragment>
      {operator.isLoading || operator.pristine ? undefined : (
        <FormOperator
          title="Modifica Operatore"
          onOk={onSubmit}
          isEdit
          onCancel={() => {
            if (onCancel) {
              onCancel();
            }
          }}
          initialValues={initialValues}
        />
      )}
    </Fragment>
  );
};

OperatorEdit.displayName = 'Operator Edit component';

export default OperatorEdit;
